import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    CheckCircle2,
    Eye,
    EyeOff,
    KeyRound,
    Loader2,
    Lock,
    Mail,
    MessageSquareLock,
    Phone,
    ShieldCheck,
    X,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";
import config from "../config";

const RULES = [
    { label: "8+ characters", test: (value) => value.length >= 8 },
    { label: "Uppercase", test: (value) => /[A-Z]/.test(value) },
    { label: "Lowercase", test: (value) => /[a-z]/.test(value) },
    { label: "Number", test: (value) => /\d/.test(value) },
    { label: "Symbol", test: (value) => /[^A-Za-z0-9\s]/.test(value) },
];

function passwordStrength(password) {
    const rules = RULES.map((rule) => ({ ...rule, passed: rule.test(password) }));
    const score = rules.filter((rule) => rule.passed).length;

    if (!password) {
        return {
            score,
            label: "Create a new password",
            bar: "bg-border",
            text: "text-muted-foreground",
            rules,
        };
    }

    if (score <= 2) {
        return {
            score,
            label: "Weak",
            bar: "bg-red-500",
            text: "text-red-500",
            rules,
        };
    }

    if (score <= 4) {
        return {
            score,
            label: "Good",
            bar: "bg-amber-500",
            text: "text-amber-500",
            rules,
        };
    }

    return {
        score,
        label: "Strong",
        bar: "bg-emerald-500",
        text: "text-emerald-500",
        rules,
    };
}

async function readJson(response) {
    const text = await response.text();

    if (!text.trim()) {
        throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
    }

    try {
        return JSON.parse(text);
    } catch {
        console.error("Invalid forgot-password response:", text);
        throw new Error("The server returned an invalid response. Check the PHP error log.");
    }
}

export default function ForgotPassword({
    requestEndpoint = `${config.API_URL}/forgot_password_request.php`,
    verifyEndpoint = `${config.API_URL}/forgot_password_verify.php`,
    resetEndpoint = `${config.API_URL}/forgot_password_reset.php`,
}) {
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [mobile, setMobile] = useState("");
    const [maskedEmail, setMaskedEmail] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [visible, setVisible] = useState({
        newPassword: false,
        confirmPassword: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");

    const strength = useMemo(
        () => passwordStrength(newPassword),
        [newPassword]
    );

    const hasConfirmation = confirmPassword.length > 0;
    const passwordsMatch =
        hasConfirmation && newPassword === confirmPassword;

    const clearError = (field) => {
        setErrors((current) => ({ ...current, [field]: "" }));
        setSuccess("");
    };

    const validateMobile = () => {
        const nextErrors = {};
        const cleanMobile = mobile.replace(/\D/g, "");

        if (!cleanMobile) {
            nextErrors.mobile = "Please enter your registered mobile number.";
        } else if (!/^[6-9]\d{9}$/.test(cleanMobile)) {
            nextErrors.mobile = "Enter a valid 10 digit Indian mobile number.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateOtp = () => {
        const nextErrors = {};

        if (!otp) {
            nextErrors.otp = "Please enter the OTP sent to your email.";
        } else if (!/^\d{6}$/.test(otp)) {
            nextErrors.otp = "OTP must be exactly 6 digits.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validatePassword = () => {
        const nextErrors = {};

        if (strength.score !== RULES.length) {
            nextErrors.newPassword =
                "Please complete all password requirements.";
        }

        if (!confirmPassword) {
            nextErrors.confirmPassword =
                "Please confirm your new password.";
        } else if (!passwordsMatch) {
            nextErrors.confirmPassword = "Passwords do not match.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const sendOtp = async (event) => {
        event.preventDefault();
        if (loading || !validateMobile()) return;

        setLoading(true);
        setSuccess("");

        try {
            const cleanMobile = mobile.replace(/\D/g, "");

            const response = await fetch(requestEndpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({ mobile: cleanMobile }),
            });

            const result = await readJson(response);

            if (!response.ok || !result.success) {
                if (result.errors && typeof result.errors === "object") {
                    setErrors(result.errors);
                }
                throw new Error(
                    result.message || "Unable to send OTP."
                );
            }

            setMaskedEmail(result.masked_email || "your registered email");
            setOtp("");
            setStep(2);

            const message =
                result.message || "OTP sent to your registered email.";
            toast.success(message);
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to connect to the server."
            );
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async (event) => {
        event.preventDefault();
        if (loading || !validateOtp()) return;

        setLoading(true);
        setSuccess("");

        try {
            const response = await fetch(verifyEndpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    mobile: mobile.replace(/\D/g, ""),
                    otp,
                }),
            });

            const result = await readJson(response);

            if (!response.ok || !result.success) {
                if (result.errors && typeof result.errors === "object") {
                    setErrors(result.errors);
                }
                throw new Error(
                    result.message || "OTP verification failed."
                );
            }

            if (!result.reset_token) {
                throw new Error(
                    "OTP verified, but the server did not return a reset token."
                );
            }

            setResetToken(result.reset_token);
            setStep(3);
            toast.success(result.message || "OTP verified successfully.");
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to connect to the server."
            );
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async (event) => {
        event.preventDefault();
        if (loading || !validatePassword()) return;

        setLoading(true);
        setSuccess("");

        try {
            const response = await fetch(resetEndpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    mobile: mobile.replace(/\D/g, ""),
                    reset_token: resetToken,
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                }),
            });

            const result = await readJson(response);

            if (!response.ok || !result.success) {
                if (result.errors && typeof result.errors === "object") {
                    setErrors(result.errors);
                }
                throw new Error(
                    result.message || "Password could not be reset."
                );
            }

            const message =
                result.message ||
                "Password reset successfully. You can now sign in.";

            setSuccess(message);
            toast.success(message);

            window.setTimeout(() => {
                navigate(result.redirect || "/login", { replace: true });
            }, 1500);
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to connect to the server."
            );
        } finally {
            setLoading(false);
        }
    };

    const resendOtp = async () => {
        if (loading) return;

        setLoading(true);
        setErrors({});
        setSuccess("");

        try {
            const response = await fetch(requestEndpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    mobile: mobile.replace(/\D/g, ""),
                }),
            });

            const result = await readJson(response);

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message || "Unable to resend OTP."
                );
            }

            setMaskedEmail(result.masked_email || maskedEmail);
            setOtp("");
            toast.success(result.message || "A new OTP has been sent.");
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to connect to the server."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
            <div className="pointer-events-none absolute inset-0 flex justify-center">
                <div className="mt-10 h-[500px] w-[500px] rounded-full bg-gradient-to-tr from-[#3BC7FF]/20 to-[#7B61FF]/20 blur-3xl" />
            </div>

            <div className="relative mx-auto w-full max-w-lg fade-in-up">
                <button
                    type="button"
                    onClick={() => {
                        if (step === 1) navigate(-1);
                        else {
                            setErrors({});
                            setSuccess("");
                            setStep((current) => current - 1);
                        }
                    }}
                    className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </button>

                <section className="glass-card rounded-3xl border border-border/70 p-6 shadow-xl sm:p-8">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl btn-gradient text-white shadow-lg">
                            {step === 1 && <KeyRound className="h-6 w-6" />}
                            {step === 2 && <MessageSquareLock className="h-6 w-6" />}
                            {step === 3 && <ShieldCheck className="h-6 w-6" />}
                        </div>

                        <h1 className="mt-4 text-2xl font-display font-bold tracking-tight sm:text-3xl">
                            {step === 1 && (
                                <>
                                    Forgot your{" "}
                                    <span className="text-gradient-brand">
                                        password?
                                    </span>
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    Verify your{" "}
                                    <span className="text-gradient-brand">
                                        OTP
                                    </span>
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    Create new{" "}
                                    <span className="text-gradient-brand">
                                        password
                                    </span>
                                </>
                            )}
                        </h1>

                        <p className="mt-2 text-sm text-muted-foreground">
                            {step === 1 &&
                                "Enter your registered mobile number to receive an OTP on your registered email."}

                            {step === 2 && (
                                <>
                                    Enter the 6 digit OTP sent to{" "}
                                    <span className="font-semibold text-foreground">
                                        {maskedEmail}
                                    </span>
                                    .
                                </>
                            )}

                            {step === 3 &&
                                "Choose a strong password you have not used before."}
                        </p>
                    </div>

                    <StepIndicator step={step} />

                    {step === 1 && (
                        <form
                            onSubmit={sendOtp}
                            className="mt-7 space-y-5"
                            noValidate
                        >
                            <div>
                                <label
                                    htmlFor="mobile"
                                    className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                                >
                                    Registered Mobile Number
                                </label>

                                <div className="relative mt-2">
                                    <Phone
                                        className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${
                                            errors.mobile
                                                ? "text-red-500"
                                                : "text-muted-foreground"
                                        }`}
                                    />

                                    <input
                                        id="mobile"
                                        name="mobile"
                                        type="tel"
                                        inputMode="numeric"
                                        maxLength={10}
                                        autoComplete="tel"
                                        value={mobile}
                                        disabled={loading}
                                        onChange={(event) => {
                                            const value = event.target.value
                                                .replace(/\D/g, "")
                                                .slice(0, 10);
                                            setMobile(value);
                                            clearError("mobile");
                                        }}
                                        placeholder="Enter 10 digit mobile number"
                                        className={`h-12 w-full rounded-xl border bg-background/60 pl-11 pr-4 text-sm outline-none transition focus:ring-2 ${
                                            errors.mobile
                                                ? "border-red-500 focus:ring-red-500/20"
                                                : "border-border focus:border-primary focus:ring-primary/25"
                                        }`}
                                    />
                                </div>

                                {errors.mobile && (
                                    <p className="mt-1.5 text-xs text-destructive">
                                        {errors.mobile}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-start gap-2 rounded-xl border border-border/70 bg-background/35 px-3 py-2.5 text-xs text-muted-foreground">
                                <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                                For your security, the OTP will be sent only to
                                the email registered with this mobile number.
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex h-12 w-full items-center justify-center gap-2 rounded-full btn-gradient font-semibold text-white ripple disabled:cursor-not-allowed disabled:opacity-65"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Checking…
                                    </>
                                ) : (
                                    <>
                                        <Mail className="h-4 w-4" />
                                        Send OTP to Email
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {step === 2 && (
                        <form
                            onSubmit={verifyOtp}
                            className="mt-7 space-y-5"
                            noValidate
                        >
                            <div>
                                <label
                                    htmlFor="otp"
                                    className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                                >
                                    Email OTP
                                </label>

                                <div className="relative mt-2">
                                    <MessageSquareLock
                                        className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${
                                            errors.otp
                                                ? "text-red-500"
                                                : "text-muted-foreground"
                                        }`}
                                    />

                                    <input
                                        id="otp"
                                        name="otp"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        value={otp}
                                        disabled={loading}
                                        onChange={(event) => {
                                            const value = event.target.value
                                                .replace(/\D/g, "")
                                                .slice(0, 6);
                                            setOtp(value);
                                            clearError("otp");
                                        }}
                                        placeholder="Enter 6 digit OTP"
                                        className={`h-12 w-full rounded-xl border bg-background/60 pl-11 pr-4 text-center text-lg font-semibold tracking-[0.35em] outline-none transition focus:ring-2 ${
                                            errors.otp
                                                ? "border-red-500 focus:ring-red-500/20"
                                                : "border-border focus:border-primary focus:ring-primary/25"
                                        }`}
                                    />
                                </div>

                                {errors.otp && (
                                    <p className="mt-1.5 text-xs text-destructive">
                                        {errors.otp}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex h-12 w-full items-center justify-center gap-2 rounded-full btn-gradient font-semibold text-white ripple disabled:cursor-not-allowed disabled:opacity-65"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Verifying…
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck className="h-4 w-4" />
                                        Verify OTP
                                    </>
                                )}
                            </button>

                            <div className="text-center">
                                <button
                                    type="button"
                                    disabled={loading}
                                    onClick={resendOtp}
                                    className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                                >
                                    Didn't receive OTP? Resend OTP
                                </button>
                            </div>
                        </form>
                    )}

                    {step === 3 && (
                        <form
                            onSubmit={resetPassword}
                            className="mt-7 space-y-5"
                            noValidate
                        >
                            <div>
                                <PasswordField
                                    id="newPassword"
                                    label="New Password"
                                    value={newPassword}
                                    error={errors.newPassword}
                                    visible={visible.newPassword}
                                    loading={loading}
                                    placeholder="Create a strong new password"
                                    onChange={(event) => {
                                        setNewPassword(event.target.value);
                                        clearError("newPassword");
                                    }}
                                    onToggle={() =>
                                        setVisible((current) => ({
                                            ...current,
                                            newPassword:
                                                !current.newPassword,
                                        }))
                                    }
                                />

                                <div className="mt-3 rounded-2xl border border-border/70 bg-background/35 p-3.5">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex flex-1 gap-1.5">
                                            {[1, 2, 3, 4, 5].map((item) => (
                                                <span
                                                    key={item}
                                                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                                                        item <= strength.score
                                                            ? strength.bar
                                                            : "bg-border"
                                                    }`}
                                                />
                                            ))}
                                        </div>

                                        <span
                                            className={`text-xs font-semibold ${strength.text}`}
                                        >
                                            {strength.label}
                                        </span>
                                    </div>

                                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                                        {strength.rules.map((rule) => (
                                            <span
                                                key={rule.label}
                                                className={`inline-flex items-center gap-1 text-[11px] ${
                                                    rule.passed
                                                        ? "text-emerald-500"
                                                        : "text-muted-foreground"
                                                }`}
                                            >
                                                {rule.passed ? (
                                                    <Check className="h-3 w-3" />
                                                ) : (
                                                    <X className="h-3 w-3" />
                                                )}
                                                {rule.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <PasswordField
                                    id="confirmPassword"
                                    label="Confirm New Password"
                                    value={confirmPassword}
                                    error={errors.confirmPassword}
                                    visible={visible.confirmPassword}
                                    loading={loading}
                                    placeholder="Enter the new password again"
                                    onChange={(event) => {
                                        setConfirmPassword(event.target.value);
                                        clearError("confirmPassword");
                                    }}
                                    onToggle={() =>
                                        setVisible((current) => ({
                                            ...current,
                                            confirmPassword:
                                                !current.confirmPassword,
                                        }))
                                    }
                                    status={
                                        hasConfirmation
                                            ? passwordsMatch
                                                ? "success"
                                                : "error"
                                            : ""
                                    }
                                />

                                {hasConfirmation && (
                                    <p
                                        className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${
                                            passwordsMatch
                                                ? "text-emerald-500"
                                                : "text-red-500"
                                        }`}
                                    >
                                        {passwordsMatch ? (
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                        ) : (
                                            <XCircle className="h-3.5 w-3.5" />
                                        )}
                                        {passwordsMatch
                                            ? "Passwords match"
                                            : "Passwords do not match"}
                                    </p>
                                )}
                            </div>

                            {success && (
                                <div
                                    className="flex items-start gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-600"
                                    role="status"
                                >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                                    {success}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || Boolean(success)}
                                className="flex h-12 w-full items-center justify-center gap-2 rounded-full btn-gradient font-semibold text-white ripple disabled:cursor-not-allowed disabled:opacity-65"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Updating password…
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck className="h-4 w-4" />
                                        Reset Password
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </section>

                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.26em] text-muted-foreground">
                    Secured by Smart Solutions
                </p>
            </div>
        </main>
    );
}

function StepIndicator({ step }) {
    const steps = [
        { number: 1, label: "Mobile" },
        { number: 2, label: "OTP" },
        { number: 3, label: "Password" },
    ];

    return (
        <div className="mt-6 flex items-center justify-center">
            {steps.map((item, index) => (
                <div key={item.number} className="flex items-center">
                    <div className="flex flex-col items-center">
                        <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold transition ${
                                step >= item.number
                                    ? "border-transparent btn-gradient text-white shadow"
                                    : "border-border bg-background/60 text-muted-foreground"
                            }`}
                        >
                            {step > item.number ? (
                                <Check className="h-4 w-4" />
                            ) : (
                                item.number
                            )}
                        </div>

                        <span
                            className={`mt-1 text-[10px] font-medium ${
                                step >= item.number
                                    ? "text-foreground"
                                    : "text-muted-foreground"
                            }`}
                        >
                            {item.label}
                        </span>
                    </div>

                    {index < steps.length - 1 && (
                        <div
                            className={`mx-2 mb-4 h-px w-12 sm:w-16 ${
                                step > item.number
                                    ? "bg-primary"
                                    : "bg-border"
                            }`}
                        />
                    )}
                </div>
            ))}
        </div>
    );
}

function PasswordField({
    id,
    label,
    value,
    error,
    visible,
    loading,
    placeholder,
    onChange,
    onToggle,
    status,
}) {
    return (
        <div>
            <label
                htmlFor={id}
                className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground"
            >
                {label}
            </label>

            <div className="relative mt-2">
                <Lock
                    className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${
                        error || status === "error"
                            ? "text-red-500"
                            : status === "success"
                            ? "text-emerald-500"
                            : "text-muted-foreground"
                    }`}
                />

                <input
                    id={id}
                    name={id}
                    type={visible ? "text" : "password"}
                    value={value}
                    disabled={loading}
                    onChange={onChange}
                    autoComplete="new-password"
                    placeholder={placeholder}
                    className={`h-12 w-full rounded-xl border bg-background/60 pl-11 pr-11 text-sm outline-none transition focus:ring-2 ${
                        error || status === "error"
                            ? "border-red-500 focus:ring-red-500/20"
                            : status === "success"
                            ? "border-emerald-500 focus:ring-emerald-500/20"
                            : "border-border focus:border-primary focus:ring-primary/25"
                    }`}
                />

                <button
                    type="button"
                    onClick={onToggle}
                    disabled={loading}
                    aria-label={visible ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground disabled:opacity-50"
                >
                    {visible ? (
                        <EyeOff className="h-4 w-4" />
                    ) : (
                        <Eye className="h-4 w-4" />
                    )}
                </button>
            </div>

            {error && (
                <p className="mt-1.5 text-xs text-destructive">{error}</p>
            )}
        </div>
    );
}
